import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  finalize,
  delay,
  map,
  retryWhen,
  scan,
  tap,
  switchMap,
  shareReplay,
  take,
  catchError,
} from 'rxjs/operators';
import { Subject, Observable, BehaviorSubject, timer, of } from 'rxjs';
import { AppConfigService } from '../../../core/config/app-config.service';
import {
  DEFAULT_PDF_SIGN_PROVIDER,
  PdfSignOtpParams,
  PdfSignParams,
  PdfSignProvider,
  PdfSignStrategy,
  PdfSignUploadParams,
  normalizePdfSignProvider,
} from './pdf-sign/pdf-sign.strategy';
import { PlatformPdfSignStrategy } from './pdf-sign/platform-pdf-sign.strategy';
import { LegacyPdfSignStrategy } from './pdf-sign/legacy-pdf-sign.strategy';
// import {saveAs} from 'file-saver';

/**
 * Gateway que sf-pdfsign consume (PDF_GATEWAY_SERVICE).
 *
 * Las URLs y el mapeo de respuestas los resuelve la estrategia activa
 * (platform o legacy, ver ./pdf-sign). Aca queda lo transversal: reintentos,
 * estado de carga y la espera a que termine la submission en curso.
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private environment: any = {};
  private submissionComplete$ = new Subject<void>();
  private documentLoadingSubject = new BehaviorSubject<boolean>(false);
  public documentLoading$ = this.documentLoadingSubject.asObservable();
  private pendingDocumentRequests = 0;
  private submissionInProgress = false;
  private inFlightBlobRequests = new Map<string, Observable<string>>();
  private lastSignedDocument: any = null;

  private strategy: PdfSignStrategy;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private platformStrategy: PlatformPdfSignStrategy,
    private legacyStrategy: LegacyPdfSignStrategy,
  ) {
    this.strategy = this.resolveStrategy(DEFAULT_PDF_SIGN_PROVIDER);
  }

  /** Proveedor activo de firma. Por defecto `platform`. */
  get provider(): PdfSignProvider {
    return this.strategy.provider;
  }

  /**
   * Cambia el proveedor de firma. Cualquier valor distinto de `legacy`
   * (incluido undefined) cae en `platform` para no alterar formularios
   * que no declaran nada.
   */
  useProvider(provider: PdfSignProvider | string | null | undefined): void {
    const next = normalizePdfSignProvider(provider);
    if (next === this.strategy.provider) return;

    if (next === 'legacy' && !this.environment?.ServidorDocumentosLegacy) {
      console.warn(
        '[DocumentService] El formulario pide signProvider "legacy" pero falta ServidorDocumentosLegacy en la configuracion; se usara ServidorDocumentos.',
      );
    }

    this.strategy = this.resolveStrategy(next);
    this.strategy.setEnviroments(this.environment);
    this.lastSignedDocument = null;
    this.inFlightBlobRequests.clear();
  }

  private resolveStrategy(provider: PdfSignProvider): PdfSignStrategy {
    return provider === 'legacy' ? this.legacyStrategy : this.platformStrategy;
  }

  setEnviroments(enviroment: any) {
    this.environment = enviroment || {};
    this.platformStrategy.setEnviroments(this.environment);
    this.legacyStrategy.setEnviroments(this.environment);
  }

  // Marca el inicio de una actualizacion de submission.
  markSubmissionStart() {
    this.submissionInProgress = true;
  }

  // Notifica que termino la actualizacion y libera requests bloqueadas.
  notifySubmissionComplete() {
    if (!this.submissionInProgress) {
      return;
    }

    this.submissionInProgress = false;
    this.submissionComplete$.next();
  }

  getBlobDocument(path: string) {
    const cacheKey = `${this.strategy.provider}:${path}`;
    const existingRequest = this.inFlightBlobRequests.get(cacheKey);
    if (existingRequest) {
      return existingRequest;
    }

    this.startDocumentLoading();

    // sf-pdfsign siempre invoca este metodo con configDocument.Ruta; cada
    // estrategia decide desde donde descargar realmente el archivo.
    const fetchBlob$ = this.strategy
      .getBlobDocument(path, this.lastSignedDocument)
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount: number, error: any) => {
              if (error.status !== 404 || retryCount >= 5) {
                throw error;
              }

              return retryCount + 1;
            }, 0),
            delay(1500),
          ),
        ),
        map((res: Blob | undefined) => (res ? URL.createObjectURL(res) : '')),
      );

    const request$ = this.waitSubmissionCompletion().pipe(
      delay(2500),
      switchMap(() => fetchBlob$),
      tap((value) => console.log('Valor emitido (res):', value)),
      finalize(() => {
        this.inFlightBlobRequests.delete(cacheKey);
        this.stopDocumentLoading();
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.inFlightBlobRequests.set(cacheKey, request$);

    return request$;
  }

  download(sPath: string) {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');
    return this.http
      .get(`${sPath}`, { headers: headers, responseType: 'blob' })
      .toPromise()
      .then((content: any) => {
        const blob = new Blob([content], { type: 'application/pdf' });
        // saveAs(blob, "documento.pdf")
      })
      .catch((err: any) =>
        console.error('Error al descargar el archivo:', err),
      );
  }

  getDococumentSign(idkatios: string, params: PdfSignParams) {
    const maxRetries = 5;
    const retryDelayMs = 1500;

    this.startDocumentLoading();

    return this.waitSubmissionCompletion().pipe(
      delay(2500),
      switchMap(() => this.strategy.getDococumentSign(idkatios, params)),
      map((document: any) => {
        if (!document?.Ruta) {
          throw new Error('Documento sin Ruta aun disponible');
        }

        this.lastSignedDocument = document;
        return document;
      }),
      retryWhen((errors) =>
        errors.pipe(
          scan((retryCount: number, error: any) => {
            if (retryCount >= maxRetries) {
              throw error;
            }

            return retryCount + 1;
          }, 0),
          delay(retryDelayMs),
        ),
      ),
      finalize(() => this.stopDocumentLoading()),
    );
  }

  private startDocumentLoading(): void {
    this.pendingDocumentRequests += 1;
    if (this.pendingDocumentRequests === 1) {
      this.documentLoadingSubject.next(true);
    }
  }

  private stopDocumentLoading(): void {
    this.pendingDocumentRequests = Math.max(
      0,
      this.pendingDocumentRequests - 1,
    );
    if (this.pendingDocumentRequests === 0) {
      this.documentLoadingSubject.next(false);
    }
  }

  sendCodeOTP(idkatios: string, params: PdfSignOtpParams): Observable<any> {
    return this.strategy.sendCodeOTP(idkatios, params);
  }

  private waitSubmissionCompletion(): Observable<void> {
    if (!this.submissionInProgress) {
      return timer(0).pipe(map(() => void 0));
    }

    return this.submissionComplete$.pipe(take(1));
  }

  getSignStatus(idKatios: string, context: Record<string, any>): Observable<boolean> {
    const idControl = context['idControl'];
    if (!idControl) return of(false);

    const base = this.appConfigService.ServicioForms;
    const url = `${base}/forms-parameters/${idKatios}/resolve`;

    return this.http.post<any>(url, { idControl, context }).pipe(
      map((response: any) => response?.data === true),
      catchError(() => of(false)),
    );
  }

  verifyCodeOTP(idkatios: string, params: PdfSignOtpParams): Observable<any> {
    return this.strategy.verifyCodeOTP(idkatios, params);
  }

  uploadSignedDocument(
    idkatios: string,
    params: PdfSignUploadParams,
  ): Observable<any> {
    return this.strategy.uploadSignedDocument(idkatios, params);
  }
}
